<template>
  <v-app class="app-background">
    <notifications group="main" data-cy="toast" />
    <v-main class="safe-area-bottom main-content">
      <transition name="page-transition" mode="out-in">
        <router-view :key="$route.fullPath" />
      </transition>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import { Submitter } from './services/submitter';
import { LocationSyncer } from './services/locationSyncer';
import firebase, { firestore } from 'firebase';
import { Submission } from '@/models/submission';
import Store from '@/store';
import { FirebaseService } from './services/firebase';
import { LocationTrackingService } from './services/locationTracking';
import CapacitorService from './services/capacitor';
import Vue from 'vue';

type Unsubscribe = (() => void) | undefined;
export default Vue.extend({
  name: 'App',
  components: {},
  data(): { listener: Unsubscribe; volunteerRegistrationListener: Unsubscribe, hotspotsListener: Unsubscribe, authStateListener: Unsubscribe, appStateListener: any, volunteerLocationsListener: Unsubscribe } {
    return {
      listener: null as any,
      volunteerRegistrationListener: null as any,
      hotspotsListener: null as any,
      authStateListener: null as any,
      appStateListener: null as any,
      volunteerLocationsListener: null as any,
    };
  },
  created(): void {
    const localFirebase = process.env.VUE_APP_LOCAL_FIREBASE;
    if (localFirebase === 'true') {
      const app = firebase.initializeApp({
        projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID || 'demo-hyperion-admin',
        apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      });
      app.firestore().settings({ host: 'localhost:8081', ssl: false });
      app.functions().useFunctionsEmulator('http://localhost:5001');
      app.auth().useEmulator('http://localhost:9099');
      app.auth().useDeviceLanguage();
    } else {
      const config = {
        apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
        authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
        databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
        projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.VUE_APP_FIREBASE_APP_ID,
      };
      firebase.initializeApp(config);
    }
    this.listener = FirebaseService.listenToCount();
    this.hotspotsListener = FirebaseService.listenToHotspots();
    this.authStateListener = firebase.auth().onAuthStateChanged(async () => {
      Store.commit('setFirebaseUser', firebase.auth().currentUser);
      this.volunteerRegistrationListener = FirebaseService.listenToRegistration();

      // Listen to volunteer locations for tracking statistics
      if (firebase.auth().currentUser?.uid) {
        this.volunteerLocationsListener = FirebaseService.listenToVolunteerLocations();

        // Try to sync any unsynced locations once on auth
        LocationSyncer.trySubmitUnsynced();
      }

      // Resume location tracking if it was previously active
      if (Store.getters.locationTrackingActive && firebase.auth().currentUser?.uid) {
        // Check if tracking has expired
        const hasExpired = await LocationTrackingService.checkAndStopIfExpired();

        if (!hasExpired) {
          // Resume tracking with existing duration
          const duration = Store.getters.trackingDuration;
          const endTime = Store.getters.trackingEndTime;
          LocationTrackingService.startTracking(duration, endTime);
        }
      }
    });

    // Listen to app state changes (background/foreground)
    // This helps with debugging and ensures expiration checks run when app comes to foreground
    CapacitorService.app.addStateChangeListener(async (state) => {

      if (state.isActive && Store.getters.locationTrackingActive) {
        // App came to foreground, check if tracking has expired
        await LocationTrackingService.checkAndStopIfExpired();
      }
    }).then((listener) => {
      this.appStateListener = listener;
    });
  },
  async beforeDestroy() {
    if (this.authStateListener) this.authStateListener();
    if (this.listener) this.listener();
    if (this.volunteerRegistrationListener) this.volunteerRegistrationListener();
    if (this.volunteerLocationsListener) this.volunteerLocationsListener();
    if (this.hotspotsListener) this.hotspotsListener();
    if (this.appStateListener) this.appStateListener.remove();
  },
  watch: {
    '$store.state.countID': {
      async handler(newCountID, oldCountID) {
        if (newCountID && newCountID !== oldCountID) {
          // Count changed, restart count-dependent listeners

          // Clean up old listeners
          if (this.listener) this.listener();
          if (this.hotspotsListener) this.hotspotsListener();
          if (this.volunteerRegistrationListener) this.volunteerRegistrationListener();
          if (this.volunteerLocationsListener) this.volunteerLocationsListener();

          // Set up new listeners for the new count
          this.listener = FirebaseService.listenToCount();
          this.hotspotsListener = FirebaseService.listenToHotspots();

          // Restart user+count dependent listeners if user is authenticated
          if (firebase.auth().currentUser?.uid) {
            this.volunteerRegistrationListener = FirebaseService.listenToRegistration();
            this.volunteerLocationsListener = FirebaseService.listenToVolunteerLocations();
          }
        }
      },
    },
  },
  methods: {
  },
});
</script>
<style scoped>
.app-background {
  background-color: #ff7a05;
}

.main-content {
  background-color: white;
  background-clip: content-box;
}
</style>

<style>
/* Notification Styles (must be non-scoped to affect vue-notification components) */
.vue-notification-group {
  top: 80px !important;
  /* Position below toolbar */
  right: 16px !important;
  width: auto !important;
  max-width: 400px !important;
}

.vue-notification {
  padding: 16px 20px !important;
  margin: 0 0 12px 0 !important;
  font-size: 14px !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border: none !important;
  min-width: 280px;
  max-width: 400px;
  backdrop-filter: blur(10px);
}

.vue-notification .notification-title {
  font-size: 15px !important;
  font-weight: 600 !important;
  margin-bottom: 4px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

.vue-notification .notification-title::before {
  content: '';
  display: inline-block;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.vue-notification.success {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
  color: white !important;
}

.vue-notification.success .notification-title::before {
  content: '✓';
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  color: #4caf50;
}

.vue-notification.warn {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important;
  color: white !important;
}

.vue-notification.warn .notification-title::before {
  content: '!';
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  color: #ff9800;
}

.vue-notification .notification-content {
  font-size: 13px !important;
  opacity: 0.95;
  line-height: 1.4;
}

/* Animation improvements */
.vue-notification {
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Mobile responsive */
@media (max-width: 768px) {
  .vue-notification-group {
    top: 70px !important;
    right: 12px !important;
    left: 12px !important;
    max-width: none !important;
  }

  .vue-notification {
    min-width: auto !important;
    max-width: none !important;
    width: 100% !important;
  }
}

/* Dark text for notification content when needed */
.vue-notification .notification-content {
  color: inherit;
}
</style>
